import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  acquisitionsApi,
  type Vendor,
  type Fund,
  type AcquisitionRequest,
} from "@/lib/api/acquisitions";
import { Loader2, Plus, Trash2, ShoppingCart } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";

type LineForm = {
  acquisitionRequestId: string;
  fundId: string;
  title: string;
  isbn: string;
  quantity: string;
  unitPrice: string;
  catalogAtReceive: boolean;
  deferCatalogEnrichment: boolean;
};

const emptyLine = (): LineForm => ({
  acquisitionRequestId: "",
  fundId: "",
  title: "",
  isbn: "",
  quantity: "1",
  unitPrice: "",
  catalogAtReceive: true,
  deferCatalogEnrichment: false,
});

function lineFormFromApprovedRequest(r: AcquisitionRequest): Partial<LineForm> {
  return {
    acquisitionRequestId: r.id,
    fundId: r.fundId ?? "",
    title: r.title,
    isbn: r.isbn ?? "",
    quantity: String(r.quantity),
    unitPrice:
      r.estimatedCost != null && r.estimatedCost !== undefined
        ? String(Number(r.estimatedCost))
        : "",
    catalogAtReceive: true,
    deferCatalogEnrichment: false,
  };
}

export default function NewPurchaseOrderPage() {
  const navigate = useNavigate();
  const { error: toastError, info: toastInfo, success: toastSuccess } = useToast();
  const [searchParams] = useSearchParams();
  const requestIdParam = searchParams.get("requestId");

  const [saving, setSaving] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(Boolean(requestIdParam));
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [vendorId, setVendorId] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);
  const [approvedRequests, setApprovedRequests] = useState<AcquisitionRequest[]>([]);

  useEffect(() => {
    Promise.all([
      acquisitionsApi.vendors.list({ isActive: true, limit: 200 }).then((r) => r.vendors),
      acquisitionsApi.funds.list({ isActive: true, limit: 200 }).then((r) => r.funds),
      acquisitionsApi.requests.list({ status: "APPROVED", limit: 200 }).then((r) => r.requests),
    ])
      .then(([v, f, ar]) => {
        setVendors(v);
        setFunds(f);
        setApprovedRequests(ar);
      })
      .catch(() => {});
  }, []);

  const loadPrefillFromRequest = useCallback(async () => {
    if (!requestIdParam) {
      setLoadingRequest(false);
      return;
    }
    setLoadingRequest(true);
    try {
      const r = await acquisitionsApi.requests.getById(requestIdParam);
      if (r.status !== "APPROVED") {
        toastInfo(
          "This request is not approved yet, so the form was not pre-filled. You can still create a purchase order manually or pick an approved request on each line.",
          "Can't pre-fill from this request"
        );
        setLines([emptyLine()]);
        navigate("/acquisitions/orders/new", { replace: true });
        return;
      }
      setLines([
        {
          acquisitionRequestId: r.id,
          fundId: r.fundId ?? "",
          title: r.title,
          isbn: r.isbn ?? "",
          quantity: String(r.quantity),
          unitPrice:
            r.estimatedCost != null && r.estimatedCost !== undefined
              ? String(Number(r.estimatedCost))
              : "",
          catalogAtReceive: true,
          deferCatalogEnrichment: false,
        },
      ]);
    } catch {
      toastError("Could not load the acquisition request.");
      setLines([emptyLine()]);
      navigate("/acquisitions/orders/new", { replace: true });
    } finally {
      setLoadingRequest(false);
    }
  }, [requestIdParam, navigate, toastError, toastInfo]);

  useEffect(() => {
    void loadPrefillFromRequest();
  }, [loadPrefillFromRequest]);

  const updateLine = (idx: number, patch: Partial<LineForm>) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (idx: number) =>
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));

  const copyLineFromAbove = (idx: number) => {
    if (idx <= 0) return;
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...prev[idx - 1] };
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!vendorId) {
      toastError("Select a vendor.");
      return;
    }
    const built = lines.map((l) => ({
      acquisitionRequestId: l.acquisitionRequestId.trim() || undefined,
      fundId: l.fundId.trim() || undefined,
      title: l.title.trim(),
      isbn: l.isbn.trim() || undefined,
      quantity: Math.max(1, parseInt(l.quantity, 10) || 1),
      unitPrice: Math.max(0, parseFloat(l.unitPrice) || 0),
      catalogAtReceive: l.catalogAtReceive,
      deferCatalogEnrichment: l.catalogAtReceive && l.deferCatalogEnrichment,
    }));
    if (built.some((l) => !l.title)) {
      toastError("Each line needs a title.");
      return;
    }

    setSaving(true);
    try {
      const po = await acquisitionsApi.orders.create({
        vendorId,
        notes: notes.trim() || undefined,
        lines: built,
      });
      toastSuccess("Purchase order created.");
      navigate(`/acquisitions/orders/${po.id}`);
    } catch (err: unknown) {
      toastError(getApiErrorMessage(err, "Could not create purchase order."));
    } finally {
      setSaving(false);
    }
  };

  if (loadingRequest) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title="New purchase order"
        description={
          requestIdParam
            ? "Review lines prefilled from the approved request, choose a vendor, then create."
            : "Choose a vendor, add one or more lines (optionally linked to approved requests), then create."
        }
        breadcrumbItems={[
          { label: "Orders", href: "/acquisitions/orders" },
          { label: "New purchase order" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Vendor</CardTitle>
          <CardDescription>
            Required. Encumbrances are created when you send the order.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label required>Vendor</Label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger>
                <SelectValue placeholder="Select vendor" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} ({v.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional internal notes"
            />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Order lines</h3>
          <Button type="button" variant="outline" size="sm" onClick={addLine}>
            <Plus className="w-4 h-4 mr-2" />
            Add line
          </Button>
        </div>

        {lines.map((line, idx) => (
          <Card key={idx}>
            <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-2">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-sm font-medium">Line {idx + 1}</CardTitle>
                {idx > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => copyLineFromAbove(idx)}
                  >
                    Copy from line {idx}
                  </Button>
                )}
              </div>
              {lines.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8 shrink-0 text-slate-400"
                  onClick={() => removeLine(idx)}
                  aria-label="Remove line"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label required>Title</Label>
                  <Input
                    value={line.title}
                    onChange={(e) => updateLine(idx, { title: e.target.value })}
                    placeholder="Title to order"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ISBN</Label>
                  <Input
                    value={line.isbn}
                    onChange={(e) => updateLine(idx, { isbn: e.target.value })}
                    className="font-mono text-sm"
                    placeholder="e.g. 978-0-13-468599-1"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Link approved request</Label>
                  <Select
                    value={line.acquisitionRequestId || "__none__"}
                    onValueChange={(v) => {
                      if (v === "__none__") {
                        updateLine(idx, { acquisitionRequestId: "" });
                        return;
                      }
                      const r = approvedRequests.find((x) => x.id === v);
                      if (r) updateLine(idx, lineFormFromApprovedRequest(r));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a request to fill this line" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__none__">None — enter manually</SelectItem>
                      {approvedRequests.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.title.length > 72 ? `${r.title.slice(0, 72)}…` : r.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Chooses title, ISBN, quantity, unit price, and fund from the request. You can
                    edit after.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    value={line.quantity}
                    onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                    placeholder="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={line.unitPrice}
                    onChange={(e) => updateLine(idx, { unitPrice: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Fund</Label>
                  <Select
                    value={line.fundId || "__none__"}
                    onValueChange={(v) => updateLine(idx, { fundId: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Optional fund for encumbrance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {funds.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name} ({f.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 sm:col-span-2">
                  <Switch
                    checked={line.catalogAtReceive}
                    onCheckedChange={(v) =>
                      updateLine(idx, {
                        catalogAtReceive: v,
                        deferCatalogEnrichment: v ? line.deferCatalogEnrichment : false,
                      })
                    }
                  />
                  <Label className="text-sm font-normal">Catalog at receive (per line)</Label>
                </div>
                {line.catalogAtReceive && (
                  <div className="flex flex-col gap-1 sm:col-span-2">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={line.deferCatalogEnrichment}
                        onCheckedChange={(v) => updateLine(idx, { deferCatalogEnrichment: v })}
                      />
                      <Label className="text-sm font-normal">
                        Hand off to cataloging first (copies use Processing status — not shown in
                        OPAC until catalog staff finish enrichment)
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate("/acquisitions/orders")}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <ShoppingCart className="w-4 h-4 mr-2" />
          )}
          Create purchase order
        </Button>
      </div>
    </PageContainer>
  );
}
